import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Search, MoreVertical } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import BottomNavigation from '../../components/Layout/BottomNavigation';
import Card from '../../components/UI/Card';

const ChatListPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSupabaseAuth();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const conversations = [
    {
      id: 1,
      name: 'Ella',
      age: 24,
      image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=200',
      lastMessage: 'Hey! How are you doing today?',
      time: '2m ago',
      isOnline: true,
      unreadCount: 2
    },
    {
      id: 2,
      name: 'Emily',
      age: 26,
      image: 'https://images.pexels.com/photos/1130626/pexels-photo-1130626.jpeg?auto=compress&cs=tinysrgb&w=200',
      lastMessage: 'Thanks for the match! 😊',
      time: '1h ago',
      isOnline: false,
      unreadCount: 0
    },
    {
      id: 3,
      name: 'Sophie',
      age: 23,
      image: 'https://images.pexels.com/photos/1542085/pexels-photo-1542085.jpeg?auto=compress&cs=tinysrgb&w=200',
      lastMessage: 'Would love to chat more about photography!',
      time: '3h ago',
      isOnline: true,
      unreadCount: 1
    },
    {
      id: 4,
      name: 'Isabella',
      age: 25,
      image: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=200',
      lastMessage: 'That sounds like a great plan!',
      time: '1d ago',
      isOnline: false,
      unreadCount: 0
    },
    {
      id: 5,
      name: 'Lily',
      age: 22,
      image: 'https://images.pexels.com/photos/1181424/pexels-photo-1181424.jpeg?auto=compress&cs=tinysrgb&w=200',
      lastMessage: 'See you tomorrow! 👋',
      time: '2d ago',
      isOnline: false,
      unreadCount: 0
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-white">Messages</h1>
          <button className="p-2 text-white hover:bg-white/10 rounded-full transition-colors">
            <MoreVertical size={20} />
          </button>
        </div>
        
        {/* Search Bar */}
        <Card className="p-3 mb-4">
          <div className="flex items-center gap-3">
            <Search size={20} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              className="flex-1 bg-transparent outline-none text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </Card>
      </div>

      {/* Conversations List */}
      <div className="px-4 space-y-3">
        {conversations.map((conversation) => (
          <Card
            key={conversation.id}
            onClick={() => navigate(`/chat/${conversation.id}`)}
            className="p-4 hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={conversation.image}
                  alt={conversation.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
                {conversation.isOnline && (
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {conversation.name} ({conversation.age})
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{conversation.time}</span>
                    {conversation.unreadCount > 0 && (
                      <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-medium">
                          {conversation.unreadCount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{conversation.lastMessage}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ChatListPage;